'use client';

import { useMemo } from 'react';
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import {
  mobileGraphql,
  mobileKeys,
  type MobileConnection,
  type MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { useAcquisitionSummary } from './useAcquisitions';
import {
  buildSearchQuery,
  mapSearchHits,
  planSearchSources,
  MIN_SEARCH_TERM_LENGTH,
  SEARCH_HITS_PER_TYPE,
  type SearchGroup,
  type SearchSource,
} from '../lib/build-search-query';

/** Requests in flight at once, so a search can't saturate the cell. */
const CONCURRENCY = 4;

export interface GlobalSearchResult {
  groups: SearchGroup[];
  /** Total matches reported across searched types. */
  total: number;
  /** Types actually queried. */
  searchedTypes: number;
  /** Types that had searchable fields but weren't queried, due to the type cap. */
  skippedTypes: number;
}

/** What the UI needs from a search backend, so the implementation can be swapped. */
export interface GlobalSearchBackend {
  /** Human label for what the results are, shown in the UI. */
  describe(): string;
  run(args: {
    term: string;
    mobileAcquisitionId: number;
    signal?: AbortSignal;
  }): Promise<GlobalSearchResult>;
}

/**
 * Search across the acquisition's artifact types.
 *
 * This fans out one `contains` query per type client-side, because
 * mobile-service has no search endpoint. The seam is deliberate: assistant-
 * service already indexes mobile content in Turbopuffer, and when that is
 * exposed as a viewer-facing endpoint it becomes a different
 * {@link GlobalSearchBackend} and nothing in the UI changes.
 */
export function useGlobalSearch(args: {
  mobileAcquisitionId: number;
  term: string;
  uiConfig: MobileUiConfigResponse | undefined;
  backend?: GlobalSearchBackend;
  /** When false the query stays idle. Defaults to on. */
  active?: boolean;
}): UseQueryResult<GlobalSearchResult, Error> & { enoughInput: boolean } {
  const { mobileAcquisitionId, term, uiConfig } = args;
  const { cellId, accessToken, enabled } = useCellServiceSession();
  const { data: summary } = useAcquisitionSummary(
    mobileAcquisitionId,
    args.active !== false && mobileAcquisitionId > 0,
  );

  const trimmed = term.trim();
  const enoughInput = trimmed.length >= MIN_SEARCH_TERM_LENGTH;

  const rowCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const stat of summary?.stats ?? []) counts.set(stat.graphqlType, stat.count);
    return counts;
  }, [summary]);

  const plan = useMemo(() => {
    const all = planSearchSources(uiConfig, rowCounts, Number.MAX_SAFE_INTEGER);
    const searched = planSearchSources(uiConfig, rowCounts);
    return { searched, skipped: all.length - searched.length };
  }, [uiConfig, rowCounts]);

  const backend = useMemo<GlobalSearchBackend>(
    () =>
      args.backend ??
      createFederatedBackend({
        cellId,
        accessToken,
        sources: plan.searched,
        skipped: plan.skipped,
      }),
    [args.backend, cellId, accessToken, plan],
  );

  const query = useQuery<GlobalSearchResult, Error>({
    queryKey: mobileKeys.globalSearch(cellId, mobileAcquisitionId, {
      term: trimmed,
      types: plan.searched.map((s) => s.graphqlType),
    }),
    enabled: enabled && args.active !== false && enoughInput && plan.searched.length > 0,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      backend.run({ term: trimmed, mobileAcquisitionId, signal }),
  });

  return Object.assign(query, { enoughInput });
}

/** Client-side fan-out backend: the only one that works against today's API. */
function createFederatedBackend(args: {
  cellId: string | null;
  accessToken: string | null;
  sources: SearchSource[];
  skipped: number;
}): GlobalSearchBackend {
  const { cellId, accessToken, sources, skipped } = args;

  return {
    describe: () =>
      `Substring match across the ${sources.length} largest searchable artifact types.`,

    async run({ term, mobileAcquisitionId, signal }) {
      const groups = await mapWithConcurrency(sources, CONCURRENCY, async (source) => {
        const query = buildSearchQuery({
          source,
          mobileAcquisitionId,
          term,
          limit: SEARCH_HITS_PER_TYPE,
        });
        if (!query) return null;

        try {
          const data = await mobileGraphql<
            Record<string, MobileConnection<Record<string, unknown>>>
          >({
            cellId: cellId as string,
            accessToken: accessToken as string,
            endpoint: source.endpoint,
            query,
            signal,
          });
          const connection = data[source.rootQuery];
          const rows = connection?.items ?? [];
          if (!rows.length) return null;
          return {
            graphqlType: source.graphqlType,
            typeLabel: source.typeLabel,
            hits: mapSearchHits(rows, source, term),
            total: connection?.total ?? rows.length,
          } satisfies SearchGroup;
        } catch {
          // One type's failure shouldn't void a search across twelve. The type
          // is simply absent from the grouped results.
          return null;
        }
      });

      const present = groups.filter((group): group is SearchGroup => group !== null);
      present.sort((a, b) => b.total - a.total);

      return {
        groups: present,
        total: present.reduce((sum, group) => sum + group.total, 0),
        searchedTypes: sources.length,
        skippedTypes: skipped,
      };
    },
  };
}

/** Run `task` over `items`, keeping at most `limit` in flight. */
async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  task: (item: T) => (Promise<R>),
): (Promise<R[]>) {
  const results = new Array<R>(items.length);
  let next = 0;

  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    for (;;) {
      const index = next++;
      if (index >= items.length) return;
      results[index] = await task(items[index]);
    }
  });

  await Promise.all(workers);
  return results;
}
